import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Layers, Server, Palette } from 'lucide-react';
import { Section, Container, SectionHead } from './Section';
import { skillGroups } from '../data/portfolio';

const iconFor = (title) => {
  if (title.toLowerCase().includes('frontend')) return <Layers size={22} />;
  if (title.toLowerCase().includes('backend')) return <Server size={22} />;
  return <Palette size={22} />;
};

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 22px;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const Card = styled(motion.div)`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.lg};
  padding: 28px 24px;
  transition: transform 250ms ease, border-color 250ms ease, background 250ms ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: -1px;
    padding: 1px;
    border-radius: inherit;
    background: linear-gradient(135deg, rgba(124,92,255,0.35), rgba(34,211,238,0.25));
    -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
            mask-composite: exclude;
    opacity: 0;
    transition: opacity 250ms ease;
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-4px);
    background: ${({ theme }) => theme.colors.surfaceHi};
  }
  &:hover::before { opacity: 1; }
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 6px;

  .icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(124,92,255,0.15);
    color: ${({ theme }) => theme.colors.primarySoft};
    border: 1px solid rgba(124,92,255,0.25);
  }

  h3 {
    font-size: 20px;
  }
  .sub {
    color: ${({ theme }) => theme.colors.textMuted};
    font-size: 13px;
  }
`;

const Items = styled.div`
  margin-top: 22px;
  display: grid;
  gap: 14px;
`;

const Item = styled.div`
  .row {
    display: flex;
    justify-content: space-between;
    font-size: 14px;
    margin-bottom: 8px;
    color: ${({ theme }) => theme.colors.textDim};
  }
  .name { color: ${({ theme }) => theme.colors.text}; font-weight: 500; }
  .bar {
    height: 6px;
    background: rgba(255,255,255,0.06);
    border-radius: 999px;
    overflow: hidden;
  }
  .fill {
    height: 100%;
    background: linear-gradient(90deg, #7C5CFF, #22D3EE);
    border-radius: 999px;
  }
`;

export default function Skills() {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.15 });

  return (
    <Section id="skills" ref={ref}>
      <Container>
        <SectionHead kicker="Skills" title="My Technical" gradient="Toolkit" sub="The stack I reach for to ship products that feel great to use." />
        <Grid>
          {skillGroups.map((g, i) => (
            <Card key={g.title} initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: i * 0.12, duration: 0.6 }}>
              <Head>
                <span className="icon">{iconFor(g.title)}</span>
                <div>
                  <h3>{g.title}</h3>
                  <div className="sub">{g.subtitle}</div>
                </div>
              </Head>
              <Items>
                {g.items.map((it, idx) => (
                  <Item key={it.name}>
                    <div className="row"><span className="name">{it.name}</span><span>{it.level}%</span></div>
                    <div className="bar">
                      <motion.div
                        className="fill"
                        initial={{ width: 0 }}
                        animate={inView ? { width: `${it.level}%` } : {}}
                        transition={{ delay: 0.3 + idx * 0.06, duration: 0.9, ease: 'easeOut' }}
                      />
                    </div>
                  </Item>
                ))}
              </Items>
            </Card>
          ))}
        </Grid>
      </Container>
    </Section>
  );
}
